import React, { useState } from 'react';
import { Wallet, TrendingDown, TrendingUp, Clock, DollarSign, Calendar, Users, AlertTriangle, CheckCircle } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadialBarChart, RadialBar } from 'recharts';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';

const Tresorerie = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // Données pour les graphiques de comparaison sur 5 ans
    const tresorerieData = [
        { annee: '2020', soldeDisponible: 145000, delaiClient: 35, delaiFournisseur: 28, bfr: 85000 },
        { annee: '2021', soldeDisponible: 165000, delaiClient: 32, delaiFournisseur: 30, bfr: 78000 },
        { annee: '2022', soldeDisponible: 195000, delaiClient: 30, delaimoisseur: 32, bfr: 65000 },
        { annee: '2023', soldeDisponible: 220000, delaiClient: 28, delaiFournisseur: 35, bfr: 55000 },
        { annee: '2024', soldeDisponible: 285000, delaiClient: 25, delaiFournisseur: 38, bfr: 42000 }
    ];

    // Données mensuelles pour le graphique d'évolution
    const evolutionMensuelle = [
        { mois: 'Jan', solde: 220000, entrees: 180000, sorties: 165000 },
        { mois: 'Fév', solde: 235000, entrees: 195000, sorties: 180000 },
        { mois: 'Mar', solde: 250000, entrees: 210000, sorties: 195000 },
        { mois: 'Avr', solde: 245000, entrees: 185000, sorties: 190000 },
        { mois: 'Mai', solde: 265000, entrees: 220000, sorties: 200000 },
        { mois: 'Juin', solde: 270000, entrees: 205000, sorties: 195000 },
        { mois: 'Juil', solde: 285000, entrees: 225000, sorties: 210000 },
        { mois: 'Août', solde: 285000, entrees: 210000, sorties: 210000 }
    ];

    // Répartition des flux de trésorerie
    const repartitionFlux = [
        { name: 'Ventes', value: 65, color: '#10b981' },
        { name: 'Prestations', value: 25, color: '#3b82f6' },
        { name: 'Autres recettes', value: 10, color: '#8b5cf6' }
    ];

    const repartitionSorties = [
        { name: 'Achats', value: 35, color: '#ef4444' },
        { name: 'Salaires', value: 30, color: '#f59e0b' },
        { name: 'Charges', value: 20, color: '#06b6d4' },
        { name: 'Investissements', value: 15, color: '#8b5cf6' }
    ];

    // Données actuelles 2024
    const currentYear = {
        soldeDisponible: 285000,
        delaiMoyenClient: 25,
        delaiMoyenFournisseur: 38,
        bfr: 42000,
        ratioLiquidite: 2.8,
        capaciteAutofinancement: 185000
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0
        }).format(value);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const calculatePercentage = (current, previous) => {
        if (!previous) return 0;
        return ((current - previous) / previous * 100).toFixed(1);
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip">
                    <p className="tooltip-label">{`${label}`}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }} className="tooltip-value">
                            {`${entry.name}: ${formatCurrency(entry.value)}`}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const CustomDelayTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip">
                    <p className="tooltip-label">{`Année ${label}`}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }} className="tooltip-value">
                            {`${entry.name}: ${entry.value} jours`}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <>
            <div className="app-container">
                <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

                <div className="main-wrapper">
                    <Navbar onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} />

                    <main className="main-content">
                        {/* Header Section */}
                        <div className="page-header">
                            <div className="header-content">
                                <div className="header-text">
                                    <h1 className="page-title">Trésorerie & Flux Financiers</h1>
                                    <p className="page-subtitle">Suivi en temps réel de votre situation de trésorerie et des flux financiers</p>
                                </div>
                            </div>
                        </div>

                        {/* KPIs Cards */}
                        <div className="kpis-grid">
                            <div className="kpi-card primary">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Solde de Trésorerie Disponible</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.soldeDisponible)}</p>
                                        <span className="kpi-trend positive">
                                            +{calculatePercentage(currentYear.soldeDisponible, tresorerieData[3].soldeDisponible)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <Wallet size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card success">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Délai Moyen Client</h3>
                                        <p className="kpi-value">{currentYear.delaiMoyenClient} jours</p>
                                        <span className="kpi-trend positive">
                                            -{Math.abs(calculatePercentage(currentYear.delaiMoyenClient, tresorerieData[3].delaiClient))}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <TrendingDown size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card warning">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Délai Moyen Fournisseur</h3>
                                        <p className="kpi-value">{currentYear.delaiMoyenFournisseur} jours</p>
                                        <span className="kpi-trend positive">
                                            +{calculatePercentage(currentYear.delaiMoyenFournisseur, tresorerieData[3].delaiFournisseur)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <TrendingUp size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card info">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Besoin en Fonds de Roulement</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.bfr)}</p>
                                        <span className="kpi-trend positive">
                                            -{Math.abs(calculatePercentage(currentYear.bfr, tresorerieData[3].bfr))}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <DollarSign size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card secondary">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Ratio de Liquidité</h3>
                                        <p className="kpi-value">{currentYear.ratioLiquidite}</p>
                                        <span className="kpi-trend positive">
                                            <CheckCircle size={16} /> Excellent
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <CheckCircle size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card danger">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Capacité d'Autofinancement</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.capaciteAutofinancement)}</p>
                                        <span className="kpi-trend positive">
                                            +12.8%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <TrendingUp size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>
                        </div>

                        {/* Charts Section */}
                        <div className="charts-section">
                            {/* Evolution de la Trésorerie sur 5 ans */}
                            <div className="chart-card full-width">
                                <div className="chart-header">
                                    <h3 className="chart-title">Évolution de la Trésorerie (5 dernières années)</h3>
                                    <div className="chart-badge">Tendance positive</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={400}>
                                        <AreaChart data={tresorerieData}>
                                            <defs>
                                                <linearGradient id="colorTresorerie" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.1} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="annee"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <YAxis
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                tickFormatter={(value) => `${(value / 1000)}kXOF`}
                                            />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Area
                                                type="monotone"
                                                dataKey="soldeDisponible"
                                                stroke="#10b981"
                                                strokeWidth={3}
                                                fillOpacity={1}
                                                fill="url(#colorTresorerie)"
                                                dot={{ fill: '#10b981', strokeWidth: 2, r: 6 }}
                                                activeDot={{ r: 8, stroke: '#10b981', strokeWidth: 2 }}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Evolution Mensuelle 2024 */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Flux de Trésorerie Mensuel 2024</h3>
                                    <div className="chart-badge secondary">Année en cours</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={350}>
                                        <BarChart data={evolutionMensuelle}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="mois"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <YAxis
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                tickFormatter={(value) => `${(value / 1000)}kXOF`}
                                            />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Bar
                                                dataKey="entrees"
                                                name="Entrées"
                                                fill="#10b981"
                                                radius={[4, 4, 0, 0]}
                                                opacity={0.8}
                                            />
                                            <Bar
                                                dataKey="sorties"
                                                name="Sorties"
                                                fill="#ef4444"
                                                radius={[4, 4, 0, 0]}
                                                opacity={0.8}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Délais Clients/Fournisseurs */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Évolution des Délais de Paiement</h3>
                                    <div className="chart-badge warning">Optimisation continue</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={350}>
                                        <LineChart data={tresorerieData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="annee"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <YAxis
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                label={{ value: 'Jours', angle: -90, position: 'insideLeft' }}
                                            />
                                            <Tooltip content={<CustomDelayTooltip />} />
                                            <Legend />
                                            <Line
                                                type="monotone"
                                                dataKey="delaiClient"
                                                name="Délai Client"
                                                stroke="#3b82f6"
                                                strokeWidth={3}
                                                dot={{ fill: '#3b82f6', strokeWidth: 2, r: 5 }}
                                                activeDot={{ r: 7 }}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="delaiFournisseur"
                                                name="Délai Fournisseur"
                                                stroke="#f59e0b"
                                                strokeWidth={3}
                                                dot={{ fill: '#f59e0b', strokeWidth: 2, r: 5 }}
                                                activeDot={{ r: 7 }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Répartition des Entrées */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Répartition des Entrées de Trésorerie</h3>
                                    <div className="chart-badge success">Structure saine</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={350}>
                                        <PieChart>
                                            <Pie
                                                data={repartitionFlux}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={120}
                                                paddingAngle={5}
                                                dataKey="value"
                                                label={({ name, value }) => `${name}: ${value}%`}
                                                labelLine={false}
                                            >
                                                {repartitionFlux.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value) => `${value}%`} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Répartition des Sorties */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Répartition des Sorties de Trésorerie</h3>
                                    <div className="chart-badge danger">Contrôle optimal</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={350}>
                                        <PieChart>
                                            <Pie
                                                data={repartitionSorties}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={120}
                                                paddingAngle={5}
                                                dataKey="value"
                                                label={({ name, value }) => `${name}: ${value}%`}
                                                labelLine={false}
                                            >
                                                {repartitionSorties.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value) => `${value}%`} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* BFR Evolution */}
                            <div className="chart-card full-width">
                                <div className="chart-header">
                                    <h3 className="chart-title">Évolution du Besoin en Fonds de Roulement</h3>
                                    <div className="chart-badge success">Optimisation réussie</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={300}>
                                        <AreaChart data={tresorerieData}>
                                            <defs>
                                                <linearGradient id="colorBFR" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                                                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="annee"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <YAxis
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                tickFormatter={(value) => `${(value / 1000)}kXOF`}
                                            />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Area
                                                type="monotone"
                                                dataKey="bfr"
                                                stroke="#8b5cf6"
                                                strokeWidth={3}
                                                fillOpacity={1}
                                                fill="url(#colorBFR)"
                                                dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 6 }}
                                                activeDot={{ r: 8, stroke: '#8b5cf6', strokeWidth: 2 }}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>

                        {/* Commentaires du Cabinet */}
                        <div className="commentaires-section">
                            <div className="commentaires-card">
                                <div className="commentaires-header">
                                    <h3 className="commentaires-title">Commentaires du Cabinet CINEF-EC</h3>
                                    <div className="commentaires-date">
                                        Dernière mise à jour: {formatDate('2024-08-20')}
                                    </div>
                                </div>
                                <div className="commentaires-content">
                                    <div className="commentaire-item">
                                        <h4 className="commentaire-title">Analyse de la Trésorerie</h4>
                                        <p className="commentaire-text">
                                            La position de trésorerie s'améliore considérablement avec un solde disponible de {formatCurrency(currentYear.soldeDisponible)},
                                            en progression de {calculatePercentage(currentYear.soldeDisponible, tresorerieData[3].soldeDisponible)}% par rapport à 2023.
                                            Cette performance témoigne d'une gestion financière rigoureuse et d'une activité commerciale soutenue.
                                        </p>
                                    </div>

                                    <div className="commentaire-item">
                                        <h4 className="commentaire-title">Optimisation des Délais</h4>
                                        <p className="commentaire-text">
                                            L'amélioration du délai client (25 jours contre 28 en 2023) et l'extension maîtrisée du délai fournisseur (38 jours)
                                            contribuent positivement à l'équilibre des flux. Le BFR continue sa tendance baissière, atteignant {formatCurrency(currentYear.bfr)},
                                            soit une réduction de {Math.abs(calculatePercentage(currentYear.bfr, tresorerieData[3].bfr))}%.
                                        </p>
                                    </div>

                                    <div className="commentaire-item">
                                        <h4 className="commentaire-title">Recommandations Stratégiques</h4>
                                        <p className="commentaire-text">
                                            Le ratio de liquidité de {currentYear.ratioLiquidite} confirme une situation financière très solide.
                                            Nous recommandons de maintenir cette politique de gestion active des créances et de poursuivre
                                            l'optimisation des délais de règlement pour sécuriser davantage la trésorerie.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

                .app-container {
                    display: flex;
                    height: 100vh;
                    
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    position: relative;
                    overflow: hidden;
                }

                .app-container::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    
                    pointer-events: none;
                }

                .main-wrapper {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    margin-left: 320px;
                    z-index: 1;
                }

                .main-content {
                    flex: 1;
                    padding: 32px;
                    overflow-y: auto;
                    scrollbar-width: thin;
                    scrollbar-color: rgba(16, 185, 129, 0.3) transparent;
                }

                .main-content::-webkit-scrollbar {
                    width: 6px;
                }

                .main-content::-webkit-scrollbar-track {
                    background: transparent;
                }

                .main-content::-webkit-scrollbar-thumb {
                    background: linear-gradient(45deg, rgba(16, 185, 129, 0.3), rgba(59, 130, 246, 0.3));
                    border-radius: 10px;
                }

                /* Header Section */
                .page-header {
                    margin-bottom: 32px;
                }

                .header-content {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }

                .header-text {
                    flex: 1;
                }

                .page-title {
                    font-size: 36px;
                    font-weight: 800;
                    margin: 0 0 8px 0;
                    background: linear-gradient(135deg, #dc3545, #008A44);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    line-height: 1.2;
                    letter-spacing: -0.8px;
                    text-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }

                .page-subtitle {
                    color: rgba(255, 255, 255, 0.8);
                    margin: 0;
                    font-size: 16px;
                    font-weight: 500;
                    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                }

                /* KPIs Grid */
       .kpis-grid {
         display: grid;
         grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
         gap: 24px;
         margin-bottom: 32px;
       }

       .kpi-card {
          background: #ffffff;
          padding: 28px;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          border: 1px solid rgba(0, 0, 0, 0.05);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
       }

       .kpi-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: currentColor;
       }

       .kpi-gradient {
          position: absolute;
          top: 0;
          right: 0;
         bottom: 0;
         left: 0;
          background: linear-gradient(135deg, currentColor, transparent);
          opacity: 0.03
       }

        .kpi-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
        }

        .kpi-card.primary { color: #dc3545; }
        .kpi-card.success { color: #10b981; }
        .kpi-card.info { color: #3b82f6; }
        .kpi-card.warning { color: #f59e0b; }
        .kpi-card.secondary { color: #8b5cf6; }
        .kpi-card.danger { color: #ef4444; }

                
                

        .kpi-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .kpi-info {
         flex: 1;
        }

        .kpi-title {
          font-size: 16px;
          font-weight: 600;
          color: #64748b;
          margin: 0 0 12px 0;
        }

        .kpi-value {
         font-size: 32px;
         font-weight: 800;
          color: #1e293b;
          margin: 0 0 8px 0;
         letter-spacing: -0.5px;
        }

        .kpi-trend {
          font-size: 14px;
          font-weight: 600;
          padding: 4px 8px;
          border-radius: 6px;
        }

        .kpi-trend.negative {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .kpi-icon {
          opacity: 0.8;
        color: currentColor;
        }

                /* Charts Section */
                .charts-section {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
                    gap: 24px;
                    margin-bottom: 40px;
                }

                .chart-card {
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(20px);
                    border-radius: 24px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    transition: all 0.3s ease;
                    overflow: hidden;
                }

                .chart-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
                }

                .chart-card.full-width {
                    grid-column: 1 / -1;
                }

                .chart-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 24px 32px 16px;
                    border-bottom: 1px solid rgba(241, 245, 249, 0.8);
                    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%);
                }

                .chart-title {
                    font-size: 18px;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0;
                    letter-spacing: -0.2px;
                }

                .chart-badge {
                    padding: 6px 16px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    background: linear-gradient(135deg, #10b981, #059669);
                    color: white;
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
                }

                .chart-badge.secondary {
                    background: linear-gradient(135deg, #3b82f6, #2563eb);
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
                }

                .chart-badge.warning {
                    background: linear-gradient(135deg, #f59e0b, #d97706);
                    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
                }

                .chart-badge.success {
                    background: linear-gradient(135deg, #10b981, #059669);
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
                }

                .chart-badge.danger {
                    background: linear-gradient(135deg, #ef4444, #dc2626);
                    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
                }

                .chart-content {
                    padding: 24px 32px 32px;
                }

                /* Custom Tooltip Styles */
                .custom-tooltip {
                    background: rgba(255, 255, 255, 0.98);
                    backdrop-filter: blur(20px);
                    border: none;
                    border-radius: 16px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
                    padding: 16px 20px;
                    border: 1px solid rgba(255, 255, 255, 0.2);
                }

                .tooltip-label {
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0 0 8px 0;
                    font-size: 14px;
                }

                .tooltip-value {
                    margin: 4px 0;
                    font-weight: 500;
                    font-size: 13px;
                }

                /* Commentaires Section */
                .commentaires-section {
                    margin-bottom: 40px;
                }

                .commentaires-card {
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(20px);
                    border-radius: 24px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    overflow: hidden;
                    transition: all 0.3s ease;
                }

                .commentaires-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
                }

                .commentaires-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 24px 32px 16px;
                    border-bottom: 1px solid rgba(241, 245, 249, 0.8);
                    background: linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%);
                }

                .commentaires-title {
                    font-size: 20px;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0;
                    letter-spacing: -0.3px;
                }

                .commentaires-date {
                    font-size: 14px;
                    color: #64748b;
                    font-weight: 500;
                }

                .commentaires-content {
                    padding: 32px;
                }

                .commentaire-item {
                    margin-bottom: 32px;
                    padding: 24px;
                    background: linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(255, 255, 255, 0.8) 100%);
                    border-radius: 16px;
                    border-left: 4px solid #8b5cf6;
                    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
                }

                .commentaire-item:last-child {
                    margin-bottom: 0;
                }

                .commentaire-title {
                    font-size: 16px;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0 0 12px 0;
                    letter-spacing: -0.2px;
                    background: linear-gradient(135deg, #8b5cf6, #3b82f6);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }

                .commentaire-text {
                    font-size: 15px;
                    line-height: 1.7;
                    color: #475569;
                    margin: 0;
                    font-weight: 400;
                }

                /* Responsive Design */
                @media (max-width: 1200px) {
                    .main-wrapper {
                        margin-left: 0;
                    }

                    .charts-section {
                        grid-template-columns: 1fr;
                    }

                    .kpis-grid {
                        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                    }
                }

                @media (max-width: 768px) {
                    .main-content {
                        padding: 16px;
                    }

                    .page-title {
                        font-size: 28px;
                    }

                    .kpis-grid {
                        grid-template-columns: 1fr;
                        gap: 16px;
                    }

                    .charts-section {
                        gap: 16px;
                    }

                    .kpi-card {
                        padding: 24px;
                    }

                    .chart-header, .chart-content {
                        padding: 20px;
                    }

                    .commentaires-header, .commentaires-content {
                        padding: 20px;
                    }

                    .commentaire-item {
                        padding: 20px;
                    }

                    .kpi-value {
                        font-size: 28px;
                    }

                    .kpi-icon {
                        width: 60px;
                        height: 60px;
                    }
                }

                @media (max-width: 480px) {
                    .page-title {
                        font-size: 24px;
                    }

                    .kpi-value {
                        font-size: 24px;
                    }

                    .chart-title {
                        font-size: 16px;
                    }

                    .commentaires-title {
                        font-size: 18px;
                    }

                    .commentaire-title {
                        font-size: 15px;
                    }
                }

                /* Animation des graphiques */
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .chart-card {
                    animation: fadeInUp 0.6s ease-out;
                }

                .kpi-card {
                    animation: fadeInUp 0.6s ease-out;
                }

                .commentaires-card {
                    animation: fadeInUp 0.6s ease-out;
                }

                /* Amélioration de l'accessibilité */
                .kpi-card:focus-within {
                    outline: 2px solid #10b981;
                    outline-offset: 2px;
                }

                .chart-card:focus-within {
                    outline: 2px solid #3b82f6;
                    outline-offset: 2px;
                }

                /* Animations supplémentaires */
                .kpi-icon {
                    transition: transform 0.3s ease;
                }

                .kpi-card:hover .kpi-icon {
                    transform: scale(1.1);
                }

                .chart-badge {
                    transition: all 0.3s ease;
                }

                .chart-card:hover .chart-badge {
                    transform: scale(1.05);
                }

                /* Style pour les seuils de couleurs */
                .recharts-legend-item {
                    font-weight: 600 !important;
                    font-size: 14px !important;
                }

                .recharts-cartesian-axis-tick-value {
                    font-weight: 500;
                    font-size: 12px;
                }

                .recharts-pie-label-text {
                    font-weight: 600;
                    font-size: 12px;
                    fill: #1e293b;
                }

                /* Effets de survol sur les graphiques */
                .recharts-active-dot {
                    stroke-width: 3 !important;
                    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
                }

                .recharts-bar:hover {
                    opacity: 0.8;
                    transition: opacity 0.2s ease;
                }

                /* Styles pour les badges de statut */
                .status-indicator {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .status-indicator.positive {
                    background: rgba(16, 185, 129, 0.1);
                    color: #059669;
                }

                .status-indicator.negative {
                    background: rgba(239, 68, 68, 0.1);
                    color: #dc2626;
                }

                .status-indicator.warning {
                    background: rgba(245, 158, 11, 0.1);
                    color: #d97706;
                }

                /* Amélioration de la lisibilité */
                ::selection {
                    background: rgba(16, 185, 129, 0.2);
                    color: #1e293b;
                }
                    }
                `}</style>
        </>
    );
};
export default Tresorerie;